import { isValidPlate, normalizePlate } from './plate'

const CONSENT_KEY = 'ov_measurement_consent_v1'
const TARGET_KEY = 'ov_consultation_target'
const CONSENT_TTL = 180 * 24 * 60 * 60 * 1000
const GTM_ID = import.meta.env.VITE_GTM_ID ?? ''
const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID ?? ''
const enabled = /^GTM-[A-Z0-9]+$/.test(GTM_ID) && /^G-[A-Z0-9]+$/.test(GA4_ID)

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

export interface MeasurementContext {
  clientId: string
  sessionId: string
  consent: { analytics: true; advertising: true; personalization: false }
  capturedAt: number
}

interface ConsultationTarget { plate: string; id?: string }

export function rememberConsultation(plate: string, id?: string): void {
  const target: ConsultationTarget = { plate: normalizePlate(plate), ...(id ? { id } : {}) }
  try { sessionStorage.setItem(TARGET_KEY, JSON.stringify(target)) } catch { /* storage optional */ }
  currentTarget = target
}

let currentTarget: ConsultationTarget | null = null
export function consultationTarget(): ConsultationTarget | null {
  if (currentTarget) return currentTarget
  try {
    const value = JSON.parse(sessionStorage.getItem(TARGET_KEY) ?? 'null') as ConsultationTarget | null
    return value && isValidPlate(value.plate) ? value : null
  } catch { return null }
}

/** Run before React, Firebase and every marketing tag. Keep private values out of URLs. */
export function prepareMeasurementLocation(): void {
  const url = new URL(window.location.href)
  const legacy = url.pathname.match(/^\/consulta\/([^/]+)\/?$/)
  if (legacy) {
    let plate = ''
    try { plate = normalizePlate(decodeURIComponent(legacy[1])) } catch { /* invalid URL stays unmeasured */ }
    if (isValidPlate(plate)) {
      const id = url.searchParams.get('c') ?? undefined
      rememberConsultation(plate, id?.match(/^consulta_[a-f0-9]{24}$/) ? id : undefined)
      url.pathname = '/consulta'
      url.search = ''
      url.hash = ''
      history.replaceState(history.state, '', url.pathname)
      return
    }
  }
  // Firebase needs these parameters to complete email-link authentication.
  // No container loads on an authentication URL; the page clears it after login.
  if (url.searchParams.has('oobCode') || url.searchParams.has('apiKey')) return
  const clean = new URLSearchParams()
  for (const key of ['gclid', 'gbraid', 'wbraid']) {
    const value = url.searchParams.get(key)
    if (value && /^[A-Za-z0-9_-]{8,256}$/.test(value)) clean.set(key, value)
  }
  for (const key of ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','matchtype','device']) {
    const value = url.searchParams.get(key)
    if (value && /^[\p{L}\p{N}_ -]{1,100}$/u.test(value)) clean.set(key, value)
  }
  url.search = clean.toString()
  url.hash = ''
  history.replaceState(history.state, '', url.pathname + url.search)
}

export function measurementConsent(): boolean | null {
  try {
    const stored = JSON.parse(localStorage.getItem(CONSENT_KEY) ?? 'null')
    return stored && typeof stored.granted === 'boolean' && typeof stored.at === 'number' &&
      Number.isFinite(stored.at) && stored.at <= Date.now() && Date.now() - stored.at < CONSENT_TTL
      ? stored.granted : null
  } catch { return null }
}

export function saveMeasurementConsent(granted: boolean): void {
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify({ granted, at: Date.now() })) } catch { /* optional */ }
  consentInMemory = granted
  lastPage = ''
  setupDataLayer()
  window.gtag('consent', 'update', consentState(granted))
  if (!granted) {
    ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA4_ID}`] = true
    for (const cookie of document.cookie.split(';')) {
      const name = cookie.trim().split('=')[0]
      if (!/^(_ga|_gcl_)/.test(name)) continue
      for (const domain of ['', `; domain=${location.hostname}`, `; domain=.${location.hostname.replace(/^www\./,'')}`]) {
        document.cookie = `${name}=; Max-Age=0; path=/${domain}; SameSite=Lax; Secure`
      }
    }
  } else {
    ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA4_ID}`] = false
    loadMeasurement()
    trackPage()
  }
  window.dispatchEvent(new Event('origem:consent-changed'))
}

let consentInMemory: boolean | null = measurementConsent()
let loaded = false
let lastPage = ''
const titles: Record<string,string> = {
  '/': 'Origem Veicular — consulta pela placa',
  '/consulta': 'Consulta veicular — Origem Veicular',
  '/minhas-consultas': 'Minhas consultas — Origem Veicular',
  '/ja-paguei': 'Acesso ao relatório — Origem Veicular',
  '/termos': 'Termos de uso — Origem Veicular',
  '/privacidade': 'Privacidade — Origem Veicular',
}

function consentState(granted: boolean) {
  return {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: 'denied',
  }
}

function safeLocation(): boolean {
  const url = new URL(location.href)
  if (!Object.hasOwn(titles, url.pathname)) return false
  return [...url.searchParams.keys()].every(key => ['gclid','gbraid','wbraid','utm_source','utm_medium','utm_campaign','utm_content','utm_term','matchtype','device'].includes(key))
}

function safeReferrer(): string {
  if (!document.referrer) return ''
  try {
    const ref = new URL(document.referrer)
    return ref.origin + (ref.origin === location.origin && Object.hasOwn(titles,ref.pathname) ? ref.pathname : '/')
  } catch { return '' }
}

function setupDataLayer(): void {
  window.dataLayer = window.dataLayer ?? []
  window.gtag = window.gtag ?? function (..._args: unknown[]) { window.dataLayer.push(arguments) }
}

export function loadMeasurement(): void {
  if (!enabled || !safeLocation() || consentInMemory !== true || loaded) return
  setupDataLayer()
  window.gtag('consent','default',consentState(true))
  window.gtag('set', { page_location: location.href, page_referrer: safeReferrer(), page_title: titles[location.pathname], allow_google_signals: false, allow_ad_personalization_signals: false })
  window.dataLayer.push({ page_location: location.href, page_referrer: safeReferrer(), page_title: titles[location.pathname] })
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(script)
  loaded = true
}

export function trackPage(): void {
  if (!safeLocation()) return
  loadMeasurement()
  if (!loaded || consentInMemory !== true || lastPage === location.pathname) return
  lastPage = location.pathname
  track('page_view')
}

const eventNames = new Set(['page_view','plate_submit','preview_success','preview_error','view_item','begin_checkout','pix_created','pix_copy','upsell_view','upsell_select','report_ready','report_error'])
export function track(name: string, params: { product_id?: string; value?: number; currency?: 'BRL'; error_code?: string; cached?: boolean; ecommerce?: unknown } = {}): void {
  if (!enabled || consentInMemory !== true || !safeLocation() || !eventNames.has(name)) return
  loadMeasurement()
  setupDataLayer()
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({ ...params, event: name, page_location: location.href, page_referrer: safeReferrer(), page_title: titles[location.pathname] })
}

export function trackProduct(name: string, product: string, amountCents: number): void {
  track(name, { product_id: product, currency: 'BRL', value: amountCents / 100, ecommerce: { currency: 'BRL', value: amountCents / 100, items: [{ item_id: product, item_name: product, price: amountCents / 100, quantity: 1 }] } })
}

/** Only consented pseudonymous GA identifiers reach the payment backend. */
export async function getMeasurementContext(): Promise<MeasurementContext | null> {
  if (!enabled || consentInMemory !== true || !safeLocation()) return null
  loadMeasurement()
  const get = (field: string) => new Promise<string | null>((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), 1600)
    window.gtag('get', GA4_ID, field, (value: unknown) => { clearTimeout(timeout); resolve(typeof value === 'string' || typeof value === 'number' ? String(value) : null) })
  })
  const [clientId,sessionId] = await Promise.all([get('client_id'),get('session_id')])
  if (!clientId || !/^\d{1,20}\.\d{1,20}$/.test(clientId) || !sessionId || !/^\d{1,16}$/.test(sessionId)) return null
  if (consentInMemory !== true) return null
  return { clientId, sessionId, consent: { analytics: true, advertising: true, personalization: false }, capturedAt: Date.now() }
}

export function openMeasurementSettings(): void { window.dispatchEvent(new Event('origem:privacy-settings')) }
